import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService } from 'primeng/api';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

// Component imports
import { SuccessStoryDialogComponent } from '../success-story-dialog/success-story-dialog.component';
import { SuccessStoryItemComponent } from '../../components/success-story-item/success-story-item.component';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner.component';

// API Service
import { SuccessStoriesApiService } from '../../services/success-stories-api.service';
import type { SuccessStoryResponseAdminDto } from '../../api/models';

@Component({
  selector: 'app-success-stories',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    MessageModule,
    SkeletonModule,
    ToastModule,
    ConfirmDialogModule,
    SuccessStoryItemComponent,
    LoadingSpinnerComponent,
  ],
  providers: [DialogService, MessageService, ConfirmationService],
  templateUrl: './success-stories.component.html',
  styleUrls: ['./success-stories.component.css'],
})
export class SuccessStoriesComponent implements OnInit, OnDestroy {
  // Injected services
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly apiService = inject(SuccessStoriesApiService);

  // Loading state
  loading = signal(true);

  successStories: SuccessStoryResponseAdminDto[] = [];
  private ref: DynamicDialogRef | undefined;

  ngOnInit(): void {
    this.loadSuccessStories();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Load success stories from API
   */
  async loadSuccessStories(): Promise<void> {
    this.loading.set(true);

    try {
      this.successStories = await this.apiService.listSuccessStories();
    } catch (error) {
      console.error('Failed to load success stories:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('successStories.errors.loadFailed'),
        life: 4000,
      });
    } finally {
      this.loading.set(false);
    }
  }

  openAddSuccessStoryDialog(): void {
    this.ref = this.dialogService.open(SuccessStoryDialogComponent, {
      header: this.translateService.instant('successStories.addTitle'),
      width: '700px',
      modal: true,
      closable: true,
      focusOnShow: false,
    });

    this.ref.onClose.subscribe(async (result: any) => {
      if (result && result.success) {
        await this.loadSuccessStories(); // Reload list

        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('successStories.success.addTitle'),
          detail: this.translateService.instant('successStories.success.addMessage'),
          life: 4000,
        });
      }
    });
  }

  openEditSuccessStoryDialog(story: SuccessStoryResponseAdminDto, index: number): void {
    this.ref = this.dialogService.open(SuccessStoryDialogComponent, {
      header: this.translateService.instant('successStories.editTitle'),
      width: '700px',
      modal: true,
      closable: true,
      data: { successStory: story },
    });

    this.ref.onClose.subscribe(async (result: any) => {
      if (result && result.success) {
        await this.loadSuccessStories(); // Reload list

        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('successStories.success.editTitle'),
          detail: this.translateService.instant('successStories.success.editMessage'),
          life: 4000,
        });
      }
    });
  }

  async removeSuccessStory(story: SuccessStoryResponseAdminDto): Promise<void> {
    if (!story.id) return;

    // Show confirmation dialog
    this.confirmationService.confirm({
      message: this.translateService.instant('successStories.confirmDelete.message', {
        title: story.title,
      }),
      header: this.translateService.instant('successStories.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.delete'),
      rejectLabel: this.translateService.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await this.apiService.deleteSuccessStory(story.id!);
          await this.loadSuccessStories(); // Reload list

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('successStories.success.removeTitle'),
            detail: this.translateService.instant('successStories.success.removeMessage'),
            life: 4000,
          });
        } catch (error) {
          console.error('Failed to delete success story:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('successStories.errors.deleteFailed'),
            life: 4000,
          });
        }
      },
    });
  }

  onEditStory(event: { story: SuccessStoryResponseAdminDto; index: number }): void {
    this.openEditSuccessStoryDialog(event.story, event.index);
  }

  onRemoveStory(event: { story: SuccessStoryResponseAdminDto; index: number }): void {
    this.removeSuccessStory(event.story);
  }

  /**
   * Get full cover image URL
   */
  getCoverImageUrl(fileId?: string): string {
    if (!fileId) return '';
    return this.apiService.getCoverImageUrl(fileId);
  }
}
